import { useIsMobile } from '@lib/hooks'
import type { FullGestureState, WebKitGestureEvent } from '@use-gesture/react'
import React from 'react'

import { runAsOnClickHandler } from '@/utils'
import useScaleEvents from './useScaleEvents'
import { useWheelZoom } from './useWheelZoom'

declare global {
  interface TouchEvent {
    clientX: number
    clientY: number
    deltaY: number
  }

  interface PointerEvent {
    deltaY: number
  }
}

type Event = Omit<FullGestureState<'pinch'>, 'event'> & {
  event: WheelEvent | PointerEvent | TouchEvent | WebKitGestureEvent
}

type UsePinchEvents = {
  onPinch: (event: Event) => void
}

const usePinchEvents = (): UsePinchEvents => {
  const lastOffset = React.useRef<number>(0)

  const isMobile = useIsMobile()

  const { onZoomIn, onZoomOut } = useScaleEvents()
  const zoomByWheel = useWheelZoom((deltaY, pointer) => {
    if (deltaY > 0) {
      onZoomOut({ diff: deltaY, pointer })
    } else {
      onZoomIn({ diff: -deltaY, pointer })
    }
  })

  const onPinch = (event: Event) => {
    event.event.preventDefault()
    event.event.stopPropagation()

    if (isMobile) {
      const currentOffset = event.offset[0]

      if (currentOffset > lastOffset.current) {
        // todo: OCT-2456
        runAsOnClickHandler(() =>
          onZoomIn({
            pointer: [event.event.clientX, event.event.clientY],
          })
        )
      }

      if (currentOffset < lastOffset.current) {
        // todo: OCT-2456
        runAsOnClickHandler(() =>
          onZoomOut({
            pointer: [event.event.clientX, event.event.clientY],
          })
        )
      }

      lastOffset.current = currentOffset

      return
    }

    if (!event.last && event.event.deltaY !== undefined) {
      zoomByWheel(event.event.deltaY, [
        event.event.clientX,
        event.event.clientY,
      ])
    }
  }

  return { onPinch }
}

export default usePinchEvents
